import type { RouteLocationNormalized } from 'vue-router';
import { useAppStore } from '@stores/app';
import { useConsentStore } from '@stores/consent';
import { userIntercomStore } from '@stores/intercom';
import { useInvestmentEntityStore } from '@stores/investment-entity';
import { useUserStore } from '@stores/user';
// API Methods - https://developers.intercom.com/installing-intercom/docs/intercom-javascript
// API Attributes & Objects - https://developers.intercom.com/installing-intercom/docs/javascript-api-attributes-objects
import { IntercomContext } from 'types/intercom';
import { isPreRender } from './client';
import { useMixpanel } from './composables/use-mixpanel';

const INTERCOM_APP_ID = 'sj0c506e';

export const initIntercomWidget = (): void => {
	if (typeof window.Intercom === 'function') return;

	const stub = function (...args: unknown[]): void {
		stub.c(args);
	} as unknown as IntercomStub;
	stub.q = [];
	stub.c = (args: unknown[]): void => {
		stub.q.push(args);
	};
	window.Intercom = stub;

	const script = document.createElement('script');
	script.type = 'text/javascript';
	script.async = true;
	script.defer = true;
	script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
	const firstScript = document.getElementsByTagName('script')[0];
	firstScript?.parentNode?.insertBefore(script, firstScript);
};

interface IntercomStub {
	(...args: unknown[]): void;
	q: unknown[][];
	c: (args: unknown[]) => void;
}

export const intercomEnabled = async (route: RouteLocationNormalized): Promise<boolean> => {
	const store = userIntercomStore();
	const appStore = useAppStore();
	const userStore = useUserStore();

	await store.storeFeatureEnabled();
	const isNonInvestor = appStore.isAuthenticated && !userStore.hasFirstInvestment;
	const isPreRenderUserAgent = isPreRender();
	const isDisabledRoute =
		appStore.isMobileWebview || route.name === 'data-visualization' || route.name === 'vcx-landing';

	const enabled = !!store.enabled && !appStore.isAdvisor && !isPreRenderUserAgent && !isDisabledRoute;

	if (isNonInvestor || store.context === 'SIGN_UP') {
		const isInTestGroup = await appStore.isUserInLDTestGroup({
			featureFlag: '2024-11-05-intercom-in-signup-a-b-test',
			testGroup: 'test-enabled-in-signup'
		});
		return enabled && isInTestGroup;
	} else {
		return enabled;
	}
};

export const registerIntercom = async (context: IntercomContext, route: RouteLocationNormalized): Promise<void> => {
	const store = userIntercomStore();
	const appStore = useAppStore();
	const consentStore = useConsentStore();
	const investmentEntityStore = useInvestmentEntityStore();
	const userStore = useUserStore();
	const { trackMixpanelEventWithBeacon } = useMixpanel();

	store.context = context;
	const canBoot =
		typeof window.Intercom === 'function' &&
		consentStore.hasPersonalizationConsent &&
		(await intercomEnabled(route));
	if (canBoot) {
		window.Intercom('boot', {
			app_id: INTERCOM_APP_ID,
			hide_default_launcher: true,
			custom_launcher_selector: '#intercom-custom-launcher',
			alignment: 'right',
			vertical_padding: 64,
			horizontal_padding: 24,
			environment: import.meta.env.VITE_INTERCOM_ENV,
			isAuthenticated: appStore.isAuthenticated,
			user_id: userStore.user?.mixpanelId,
			fundrise_user_id: userStore.user?.id,
			zendeskId: userStore.user?.zendeskId,
			user_hash: userStore.user?.intercomIdentityVerification,
			// name: [user.user?.firstName, user.user?.lastName].join(' '),
			// email: user.user?.email,
			// phone: user.phoneData.currentPhoneNumber,
			context,
			investor: userStore.hasFirstInvestment,
			advisor: userStore.user?.isInvestmentAdvisor ?? false,
			accredited: investmentEntityStore.isVerifiedAccredited,
			premium: userStore.isPremiumUser,
			created_at: userStore.user?.entityDetails?.firstInvestmentDate
		});

		window.Intercom('onShow', () => trackMixpanelEventWithBeacon('Intercom', { event: 'Show' }));
		window.Intercom('onHide', () => trackMixpanelEventWithBeacon('Intercom', { event: 'Hide' }));

		appStore.toggleIntercom(true);
	} else {
		await shutdownIntercom();
	}
};

export const shutdownIntercom = async (): Promise<void> => {
	const appStore = useAppStore();
	if (typeof window.Intercom === 'function') {
		window.Intercom('shutdown');
	}
	appStore.toggleIntercom(false);
};

export const intercomShowNewMessage = (prepopulateMessage: string): void => {
	if (typeof window.Intercom === 'function') {
		window.Intercom('showNewMessage', prepopulateMessage);
	}
};
